import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

/**
 * TranslationFragmentService
 *
 * Loads and merges translation fragment files (namespaces) on demand
 * and ensures they stay in sync when the active language changes.
 *
 * Fragment files live under `/assets/i18n/<namespace>/<lang>.json`.
 * Example: `/assets/i18n/access-time/pt-BR.json`.
 */
@Injectable({ providedIn: 'root' })
export class TranslationFragmentService {
  private readonly http = inject(HttpClient);
  private readonly translate = inject(TranslateService);

  /** Keeps track of which namespaces have been registered to auto-load on language changes */
  private readonly registeredNamespaces = new Set<string>();

  /** Cache of loaded namespaces per language to avoid duplicate network calls */
  private readonly loadedByLang = new Map<string, Set<string>>();

  /** Cache for module-relative loads: key = `${moduleKey}|${lang}` */
  private readonly moduleLangLoaded = new Set<string>();
  /** Track watchers for module-relative loads to avoid duplicate subscriptions */
  private readonly moduleWatchers = new Set<string>();

  constructor() {
    // Load registered namespaces whenever language changes
    this.translate.onLangChange.subscribe(({ lang }) => {
      this.loadRegisteredNamespacesForLang(lang);
    });
  }

  /** Ensure a single namespace is loaded for the current language and registered for future changes */
  public ensureNamespace(namespace: string): void {
    if (!namespace || namespace.trim().length === 0) return;
    this.registeredNamespaces.add(namespace);
    const lang = this.translate.currentLang || this.translate.getDefaultLang();
    this.loadNamespaceForLang(namespace, lang);
  }

  /** Ensure multiple namespaces are loaded and registered */
  public ensureNamespaces(namespaces: string[]): void {
    (namespaces ?? []).forEach((ns) => this.ensureNamespace(ns));
  }

  private loadRegisteredNamespacesForLang(lang: string): void {
    this.registeredNamespaces.forEach((ns) => this.loadNamespaceForLang(ns, lang));
  }

  private loadNamespaceForLang(namespace: string, lang: string): void {
    if (!lang) return;
    const alreadyLoadedForLang = this.loadedByLang.get(lang) ?? new Set<string>();
    if (alreadyLoadedForLang.has(namespace)) return;

    const url = `/assets/i18n/${namespace}/${lang}.json`;
    this.http.get<Record<string, unknown>>(url).subscribe({
      next: (data) => {
        this.translate.setTranslation(lang, data || {}, true);
        alreadyLoadedForLang.add(namespace);
        this.loadedByLang.set(lang, alreadyLoadedForLang);
      },
      error: () => {
        // Fail silently if fragment not found for the language
      }
    });
  }

  /**
   * Registers inline translation fragments without HTTP fetch.
   * The object should be of the form: { 'pt-BR': {...}, 'en-US': {...}, ... }
   */
  public registerInline(translationsByLang: Record<string, Record<string, unknown>>): void {
    Object.entries(translationsByLang || {}).forEach(([lang, data]) => {
      if (data && typeof data === 'object') {
        this.translate.setTranslation(lang, data, true);
      }
    });
  }

  /**
   * Loads translation JSONs colocated with a component using module-relative URLs.
   * Place files under `<component-folder>/<relativeFolder>/<lang>.json` and call:
   *   loadModuleFragments(import.meta.url, './i18n')
   *
   * By default, it loads for the current language and will auto-load on language changes
   * while the app is running (avoids duplicates).
   */
  public loadModuleFragments(
    callerModuleUrl: string,
    relativeFolder: string,
    options?: { watchLangChange?: boolean; availableLangs?: string[] }
  ): void {
    const folder = relativeFolder?.endsWith('/') ? relativeFolder : `${relativeFolder}`;
    const watch = options?.watchLangChange !== false;
    const available = options?.availableLangs;
    const moduleKey = `${callerModuleUrl}|${folder}`;

    const loadForLang = (lang: string) => {
      if (!lang) return;
      if (available && !available.includes(lang)) return;
      const cacheKey = `${moduleKey}|${lang}`;
      if (this.moduleLangLoaded.has(cacheKey)) return;
      try {
        const url = new URL(`${folder}/${lang}.json`, callerModuleUrl).toString();
        this.http.get<Record<string, unknown>>(url).subscribe({
          next: (data) => {
            this.translate.setTranslation(lang, data || {}, true);
            this.moduleLangLoaded.add(cacheKey);
          },
          error: () => {
            // Missing localized file is non-fatal
          }
        });
      } catch {
        // Invalid URL — ignore
      }
    };

    // Load for current language immediately
    loadForLang(this.translate.currentLang || this.translate.getDefaultLang());

    if (watch && !this.moduleWatchers.has(moduleKey)) {
      this.moduleWatchers.add(moduleKey);
      this.translate.onLangChange.subscribe(({ lang }) => loadForLang(lang));
    }
  }

  /**
   * Loads translation JSONs from a known assets folder path.
   * Example: loadAssetsFolder('/assets/i18n/access-time')
   */
  public loadAssetsFolder(
    basePath: string,
    options?: { watchLangChange?: boolean; availableLangs?: string[] }
  ): void {
    const watch = options?.watchLangChange !== false;
    const available = options?.availableLangs;
    const key = `assets:${basePath}`;

    const loadForLang = (lang: string) => {
      if (!lang) return;
      if (available && !available.includes(lang)) return;
      const cacheKey = `${key}|${lang}`;
      if (this.moduleLangLoaded.has(cacheKey)) return;
      const url = `${basePath}/${lang}.json`;
      this.http.get<Record<string, unknown>>(url).subscribe({
        next: (data) => {
          this.translate.setTranslation(lang, data || {}, true);
          this.moduleLangLoaded.add(cacheKey);
        },
        error: () => {
          // Missing file is non-fatal
        }
      });
    };

    loadForLang(this.translate.currentLang || this.translate.getDefaultLang());

    if (watch && !this.moduleWatchers.has(key)) {
      this.moduleWatchers.add(key);
      this.translate.onLangChange.subscribe(({ lang }) => loadForLang(lang));
    }
  }
}


